import type { QueryClient } from '@tanstack/react-query';

import type { PetitionDraft } from '#src/app/shared/api/petitionDraft';
import type { PetitionAngleData } from '#src/app/shared/api/socialStarter';
import { formatAngleDescriptionForSapEditor } from '#src/app/shared/utils/socialStarter';

import {
	createPersistentDraftContext,
	getDraftImage,
	type ImagePreparation,
	type DraftContext,
} from './imagePreparation';

function isReusableCurrentDraft(
	preparation: ImagePreparation,
	currentDraft: PetitionDraft | null,
	socialStarterId: string,
): currentDraft is PetitionDraft {
	return !!preparation.reusableDraftContext && !!currentDraft && currentDraft.metadata?.angleToken === socialStarterId;
}

async function getReusableCurrentDraft(preparation: ImagePreparation): Promise<PetitionDraft | null> {
	const context = preparation.reusableDraftContext;
	return context ? context.draftClient.getDraft(context.authorId) : null;
}

function createDraftData(
	preparation: ImagePreparation,
	draft: PetitionAngleData,
	socialStarterId: string,
	currentDraft: PetitionDraft | null,
) {
	const existingImage = currentDraft ? getDraftImage(currentDraft) : undefined;
	return {
		type: 'user_draft' as const,
		petitionDraft: {
			ask: draft.ask,
			description: formatAngleDescriptionForSapEditor(draft.description),
			...(existingImage ? { image: existingImage } : {}),
		},
		metadata: {
			...(currentDraft?.metadata ?? {}),
			angleToken: socialStarterId,
			imageJobId: existingImage ? null : preparation.imageJobId,
			imageGenerationCount: preparation.imageGenerationCount,
		},
	};
}

async function writeClaimedDraft(
	context: DraftContext,
	canReuseCurrentDraft: boolean,
	draftData: ReturnType<typeof createDraftData>,
): Promise<void> {
	if (canReuseCurrentDraft) {
		await context.draftClient.updateDraft(context.authorId, draftData);
		return;
	}
	await context.draftClient.createDraft(context.authorId, draftData);
}

export async function saveClaimedDraft(
	preparation: ImagePreparation,
	draft: PetitionAngleData,
	socialStarterId: string,
	queryClient: QueryClient,
): Promise<void> {
	const currentDraft = await getReusableCurrentDraft(preparation);
	const canReuseCurrentDraft = isReusableCurrentDraft(preparation, currentDraft, socialStarterId);
	const context = preparation.reusableDraftContext ?? createPersistentDraftContext(queryClient);
	const draftData = createDraftData(preparation, draft, socialStarterId, canReuseCurrentDraft ? currentDraft : null);

	await writeClaimedDraft(context, canReuseCurrentDraft, draftData);
}
